/**
 * The Labs preview hand-off, as the sign-up page reads it.
 *
 * A reader who finishes a lab on a guide page and taps "create your account" lands here with the
 * campaign tag, the guide, the vertical and the matched externship already in the URL (see
 * `signUpHref` in the preview payoff). Their RESULT did not travel: the lab runs inside the guide
 * page's frame, whose storage the browser partitions away from this origin, so the archetype, the
 * lean and the fingerprint lived only in the payoff's state. Four short parameters carry them now.
 *
 * 🔴 NOTHING PERSONAL GOES IN THESE PARAMETERS. Archetype ids, small integers and slider positions
 * only. The reader's own words (their one-line diagnosis) stay off the URL on purpose.
 *
 * 🔴 THE SWITCH IS THE CAMPAIGN TAG IN THE URL, NEVER THE PERSISTED ONE. `useUtmParams` remembers
 * `utm_campaign` in localStorage for attribution. Reading it from there would show the rail on a plain
 * visit a week after the lab. Only a URL that names the campaign AND carries a result gets the rail;
 * everyone else sees sign-up exactly as it ships, so the live sign-up A/B test gains no third condition.
 *
 * This module is deliberately light (no packs, no React) because the layout switcher imports it on
 * every sign-up load. Pack resolution happens in the lazily loaded arrival layout.
 */
import { prefilledEmail } from '@/app/(auth)/_utils/prefilled-email';

export const LABS_ARRIVAL_CAMPAIGN = 'labs_preview';

export const LABS_ARRIVAL_PARAMS = {
  archetype: 'lab',
  fingerprint: 'lab_fp',
  lean: 'lab_lean',
  score: 'lab_score',
} as const;

/** Slider scale top end. Matches the engine's 0..6 sliders and the artifact's FINGERPRINT_MAX. */
export const FINGERPRINT_MAX = 6;
const MAX_FINGERPRINT_ROWS = 8;
const MAX_COUNT = 99;
const ID_RE = /^[a-z0-9-]{1,32}$/;
const SMALL_INT_RE = /^\d{1,2}$/;
const EXTERNSHIP_ID_RE = /^\d{1,9}$/;
const SEP = '.';
/** An unanswered slider in `lab_fp`, so the entries after it keep their positions. */
const SKIPPED = 'x';

export interface LabsReadoutHandoff {
  archetypeId: string;
  /**
   * One entry per mindset slider IN THE PACK'S QUESTION ORDER, 0..6, null for a slider the reader
   * skipped. Positional on purpose: the rail plots entry N on question N, so a gap has to stay a gap
   * (Bugbot on #2990: dropping unanswered sliders shifted later dots onto the wrong questions).
   */
  fingerprint: readonly (number | null)[];
  /** Right-swipe counts per pole, or null when nothing was right-swiped. */
  lean: { aCount: number; bCount: number } | null;
  /** Rates right over rates answered, or null when none were answered. */
  score: { answered: number; right: number } | null;
}

/** The hand-off as read from the URL, before any pack is consulted. */
export interface LabsArrivalParams extends LabsReadoutHandoff {
  /** '' when the URL carries no usable address. */
  email: string;
  externshipId: number | null;
  /** The guide page slug from `utm_source`, or '' when absent. */
  guideSource: string;
  /** The vertical slug from `utm_content`. */
  vertical: string;
}

const clampInt = (value: number, max: number): number => Math.min(Math.max(Math.round(value), 0), max);

/** The URL parameters that carry a readout. Everything absent or empty is simply left out. */
export function encodeLabsReadout(readout: LabsReadoutHandoff): Record<string, string> {
  const params: Record<string, string> = { [LABS_ARRIVAL_PARAMS.archetype]: readout.archetypeId };
  if (readout.lean) {
    params[LABS_ARRIVAL_PARAMS.lean] = [
      clampInt(readout.lean.aCount, MAX_COUNT),
      clampInt(readout.lean.bCount, MAX_COUNT),
    ].join(SEP);
  }
  if (readout.fingerprint.some((value) => value !== null)) {
    params[LABS_ARRIVAL_PARAMS.fingerprint] = readout.fingerprint
      .slice(0, MAX_FINGERPRINT_ROWS)
      .map((value) => (value === null ? SKIPPED : String(clampInt(value, FINGERPRINT_MAX))))
      .join(SEP);
  }
  if (readout.score && readout.score.answered > 0) {
    params[LABS_ARRIVAL_PARAMS.score] = [
      clampInt(readout.score.right, MAX_COUNT),
      clampInt(readout.score.answered, MAX_COUNT),
    ].join(SEP);
  }
  return params;
}

function parseInts(raw: string | null, count: number | null, max: number): number[] | null {
  const values = parseSlots(raw, count, max, false);
  return values ? values.map((value) => value ?? 0) : null;
}

/** Dot-separated small ints, optionally allowing `x` for a skipped slot (kept as null). */
function parseSlots(
  raw: string | null,
  count: number | null,
  max: number,
  allowSkipped: boolean,
): (number | null)[] | null {
  if (!raw) {
    return null;
  }
  const parts = raw.split(SEP);
  if (count !== null && parts.length !== count) {
    return null;
  }
  if (parts.length === 0 || parts.length > MAX_FINGERPRINT_ROWS) {
    return null;
  }
  const values = parts.map((part) => {
    if (allowSkipped && part === SKIPPED) {
      return null;
    }
    return SMALL_INT_RE.test(part) ? Number(part) : Number.NaN;
  });
  if (values.some((value) => value !== null && (!Number.isInteger(value) || value < 0 || value > max))) {
    return null;
  }
  return values;
}

/**
 * The Labs arrival the URL describes, or null for every other sign-up load.
 *
 * Tolerant of a damaged optional (a lean or fingerprint that does not parse is dropped, the rail
 * simply omits that block) and strict on the two things the rail cannot render without: the campaign
 * tag and a well-formed archetype id with a vertical to look it up in.
 */
export function parseLabsArrival(searchParams: Pick<URLSearchParams, 'get'> | null): LabsArrivalParams | null {
  if (!searchParams || searchParams.get('utm_campaign') !== LABS_ARRIVAL_CAMPAIGN) {
    return null;
  }
  const archetypeId = searchParams.get(LABS_ARRIVAL_PARAMS.archetype)?.trim() ?? '';
  const vertical = searchParams.get('utm_content')?.trim() ?? '';
  if (!(ID_RE.test(archetypeId) && ID_RE.test(vertical))) {
    return null;
  }

  const leanValues = parseInts(searchParams.get(LABS_ARRIVAL_PARAMS.lean), 2, MAX_COUNT);
  const lean =
    leanValues && leanValues[0] !== undefined && leanValues[1] !== undefined && leanValues[0] + leanValues[1] > 0
      ? { aCount: leanValues[0], bCount: leanValues[1] }
      : null;

  const fingerprint = parseSlots(searchParams.get(LABS_ARRIVAL_PARAMS.fingerprint), null, FINGERPRINT_MAX, true) ?? [];

  const scoreValues = parseInts(searchParams.get(LABS_ARRIVAL_PARAMS.score), 2, MAX_COUNT);
  const score =
    scoreValues &&
    scoreValues[0] !== undefined &&
    scoreValues[1] !== undefined &&
    scoreValues[1] > 0 &&
    scoreValues[0] <= scoreValues[1]
      ? { right: scoreValues[0], answered: scoreValues[1] }
      : null;

  const rawExternshipId = searchParams.get('externshipId');
  const externshipId = rawExternshipId && EXTERNSHIP_ID_RE.test(rawExternshipId) ? Number(rawExternshipId) : null;

  const guideSource = searchParams.get('utm_source')?.trim().slice(0, 120) ?? '';

  return {
    archetypeId,
    email: prefilledEmail(searchParams),
    externshipId,
    fingerprint,
    guideSource,
    lean,
    score,
    vertical,
  };
}
